import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import UserPackagesManagementApi from '@src/apis/userPackagesManagement';
import { COLOR } from '@src/styles/color';

const UpdateUserPackageModal = ({ open, userPackage, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [selectedPackageId, setSelectedPackageId] = useState('');
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingPackages, setLoadingPackages] = useState(false);

  const fetchPackages = async () => {
    try {
      setLoadingPackages(true);
      const response = await UserPackagesManagementApi.getPackages();

      if (response?.data || response?.packages) {
        setPackages(response.data || response.packages || []);
      }
    } catch (error) {
      enqueueSnackbar(t('fetchPackagesError'), { variant: 'error' });
    } finally {
      setLoadingPackages(false);
    }
  };

  const handleClose = () => {
    setSelectedPackageId('');
    setLoading(false);
    onClose();
  };

  const handleSubmit = async () => {
    if (!selectedPackageId) {
      enqueueSnackbar(t('pleaseSelectPackage'), { variant: 'error' });
      return;
    }

    try {
      setLoading(true);
      const response = await UserPackagesManagementApi.updateUserPackage({
        userId: userPackage.userId.id,
        packageId: selectedPackageId,
      });

      if (response?.status) {
        enqueueSnackbar(t('updateUserPackageSuccess'), {
          variant: 'success',
        });
        onSuccess();
      }
    } catch (error) {
      enqueueSnackbar(t('updateUserPackageError'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchPackages();
      if (userPackage?.packageId?.id) {
        setSelectedPackageId(userPackage.packageId.id);
      }
    }
  }, [open, userPackage]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: `1px solid ${COLOR.charcoal[16]}`,
          padding: '24px 24px 16px 24px',
        }}
      >
        <Typography variant="h5" sx={{ color: COLOR.text.primary }}>
          {t('updateUserPackage')}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ padding: '24px' }}>
        <Box sx={{ marginBottom: '24px' }}>
          <Typography variant="body2" sx={{ marginBottom: '8px' }}>
            {t('userName')}:{' '}
            <strong>{userPackage?.userId?.name || '--'}</strong>
          </Typography>
          <Typography variant="body2" sx={{ marginBottom: '16px' }}>
            {t('currentPackage')}:{' '}
            <strong>{userPackage?.packageId?.name || '--'}</strong>
          </Typography>
        </Box>

        <FormControl fullWidth sx={{ marginBottom: '16px' }}>
          <InputLabel id="package-select-label">
            {t('selectPackage')}
          </InputLabel>
          <Select
            labelId="package-select-label"
            value={selectedPackageId}
            label={t('selectPackage')}
            onChange={(e) => setSelectedPackageId(e.target.value)}
            sx={{
              backgroundColor: COLOR.white[100],
              borderRadius: '8px',
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: COLOR.primary.light,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: COLOR.primary.main,
              },
            }}
          >
            {loadingPackages ? (
              <MenuItem disabled>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <CircularProgress size={16} />
                  {t('loading')}
                </Box>
              </MenuItem>
            ) : (
              packages.map((pkg) => (
                <MenuItem key={pkg.id} value={pkg.id}>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {pkg.name}
                    </Typography>
                    {pkg.packageCode && (
                      <Typography
                        variant="body2"
                        sx={{ color: COLOR.text.secondary }}
                      >
                        {pkg.packageCode} -{' '}
                        {pkg.price ? `$${pkg.price}` : t('free')}
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>
      </DialogContent>

      <DialogActions
        sx={{
          padding: '16px 24px 24px 24px',
          borderTop: `1px solid ${COLOR.charcoal[16]}`,
          gap: '12px',
        }}
      >
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            color: COLOR.text.secondary,
            borderColor: COLOR.charcoal[32],
            textTransform: 'none',
            fontWeight: 500,
            '&:hover': {
              borderColor: COLOR.charcoal[48],
              backgroundColor: COLOR.charcoal[8],
            },
          }}
        >
          {t('cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !selectedPackageId}
          sx={{
            background: `linear-gradient(135deg, ${COLOR.primary.main} 0%, ${COLOR.primary.dark} 100%)`,
            textTransform: 'none',
            fontWeight: 600,
            padding: '10px 24px',
            '&:hover': {
              background: `linear-gradient(135deg, ${COLOR.primary.dark} 0%, ${COLOR.primary.main} 100%)`,
            },
            '&:disabled': {
              background: COLOR.charcoal[32],
              color: COLOR.text.disabled,
            },
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <CircularProgress size={16} color="inherit" />
              {t('updating')}
            </Box>
          ) : (
            t('update')
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateUserPackageModal;
